import { TAX_REGISTRATION_LABELS, WORK_LOCATION_TYPE_LABELS, type WorkLocationResponse } from '../types';
import { registrationWarning } from './registrationWarning';
import { stateName } from './usStateNames';

function typeBadgeClass(location: WorkLocationResponse): string {
  if (location.isDefault) return 'badge-main';
  switch (location.locationType) {
    case 'RemoteHub': return 'badge-remote';
    case 'ClientSite': return 'badge-client';
    case 'Temporary': return 'badge-temp';
    default: return 'badge-branch';
  }
}

function typeLabel(location: WorkLocationResponse): string {
  if (location.isDefault) return 'Main';
  return location.locationType ? WORK_LOCATION_TYPE_LABELS[location.locationType] ?? location.locationType : '—';
}

/** Badge for the registration column. A location that is not in use for payroll and has no record reads "Not Applicable", never a misleading status. */
function taxBadge(location: WorkLocationResponse): { label: string; className: string } {
  const inactive = location.status !== 'Active';
  if (inactive && !location.taxRegistration) return { label: 'Not Applicable', className: 'badge-neutral' };
  switch (location.taxRegistrationStatus) {
    case 'Registered': return { label: TAX_REGISTRATION_LABELS.Registered, className: 'badge-active' };
    case 'InProgress': return { label: TAX_REGISTRATION_LABELS.InProgress, className: 'badge-progress' };
    case 'NotRequired': return { label: TAX_REGISTRATION_LABELS.NotRequired, className: 'badge-dark' };
    default: return { label: TAX_REGISTRATION_LABELS.NotRegistered, className: 'badge-danger' };
  }
}

/** Bolds the state name inside the warning ("This location is in **California** ..."), as the Figma does. */
function emphasize(text: string, word: string) {
  if (!word) return text;
  const index = text.indexOf(word);
  if (index < 0) return text;
  return (
    <>
      {text.slice(0, index)}
      <strong>{word}</strong>
      {text.slice(index + word.length)}
    </>
  );
}

interface LocationCardProps {
  location: WorkLocationResponse;
  readOnly: boolean;
  onEdit: (location: WorkLocationResponse, tab?: 'branch' | 'registration') => void;
  onArchive: (location: WorkLocationResponse) => void;
  onReactivate: (location: WorkLocationResponse) => void;
}

/** One location row (Figma S01): name + type badge + address, Status for Payroll, Tax Registration, Actions, and the incomplete-registration warning. */
export function LocationCard({ location, readOnly, onEdit, onArchive, onReactivate }: LocationCardProps) {
  const inactive = location.status !== 'Active';
  const warning = registrationWarning(location);
  const tax = taxBadge(location);
  const stateText = stateName(location.state);

  return (
    <div className={`loc-card${inactive ? ' inactive' : ''}${warning ? ' warn' : ''}`} data-testid={`location-card-${location.id}`}>
      <div className="loc-body">
        <div>
          <div className="loc-name">
            {location.name} <span className={`badge ${typeBadgeClass(location)}`}>{typeLabel(location)}</span>
          </div>
          <div className="loc-address">
            <i className="fa fa-map-marker bps-icon" aria-hidden="true" />
            {location.addressLine1}, {location.city}, {location.state} {location.zip}
            <span className="sr-only"> ({stateText})</span>
          </div>
        </div>
        <div>
          <div className="loc-col-label">Status for Payroll:</div>
          <span className={`badge ${inactive ? 'badge-neutral' : 'badge-active'}`}>{inactive ? 'Inactive' : 'Active'}</span>
        </div>
        <div>
          <div className="loc-col-label">Tax Registration:</div>
          <span className={`badge ${tax.className}`}>{tax.label}</span>
        </div>
        <div className="loc-actions-col">
          <div className="loc-col-label">Actions:</div>
          {!readOnly && (
            <div className="loc-actions">
              {inactive ? (
                <button type="button" className="action green" onClick={() => onReactivate(location)}>
                  <i className="fa fa-undo bps-icon" aria-hidden="true" />
                  Reactivate
                </button>
              ) : (
                <>
                  <button type="button" className="action outline" onClick={() => onEdit(location)} aria-label={`Edit ${location.name}`}>
                    <i className="fa fa-pencil bps-icon" aria-hidden="true" />
                    Edit
                  </button>
                  {!location.isDefault && (
                    <button type="button" className="action danger outline" onClick={() => onArchive(location)} aria-label={`Archive ${location.name}`}>
                      <i className="fa fa-archive bps-icon" aria-hidden="true" />
                      Archive
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {warning && (
        <div className="loc-warning" role="status">
          <i className="fa fa-exclamation-triangle bps-icon" aria-hidden="true" />
          <span>{emphasize(warning, stateText)}</span>
          {!readOnly && (
            <button type="button" className="complete-registration" onClick={() => onEdit(location, 'registration')}>
              <i className="fa fa-dot-circle-o bps-icon" aria-hidden="true" />
              Complete Registration
            </button>
          )}
        </div>
      )}
    </div>
  );
}
